import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { ImageUploadBox, ImageUploader } from '@service/seller/components/ImageUploadBox';
import {
  giftDeliveryCategoryDataAtomFamily,
  giftDeliveryCategoryListDataAtom,
  giftDeliveryCategoryStatusAtomFamily,
} from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.atom';
import {
  createGiftDeliveryCategory,
  updateGiftDeliveryCategory,
} from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.controller';
import { useGiftDeliveryCategoryHook } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.hook';
import { Image } from '@shared/api/media/media.interface';
import { postImage } from '@shared/api/uploadImage';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { invalidateQueriesByUrlPart } from '@shared/hooks/recoil-query';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { useQueryClient } from 'react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import styles from '../components/GiftBrandManagement.module.css';

const genKey = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `img-${Math.random().toString(36).slice(2)}`;

/** 폼 내부에서 다루는 이미지 항목 (기존 업로드 이미지 또는 새로 선택한 파일) */
interface ImageItem {
  key: string; // 렌더 안정용 로컬 키
  image: Image | null; // 기존 업로드 이미지(populated)
  file: File | null; // 새로 선택한 파일
  preview: string; // 새 파일 object URL
}

// 새 파일들을 ImageItem 으로 변환 (object URL 미리보기 생성)
const filesToImageItems = (files: File[]): ImageItem[] =>
  files.map((file) => ({
    key: genKey(),
    image: null,
    file,
    preview: URL.createObjectURL(file),
  }));

// 이미지 항목 순서 이동
const moveImageItem = (
  setter: Dispatch<SetStateAction<ImageItem[]>>,
  key: string,
  dir: -1 | 1,
) =>
  setter((prev) => {
    const idx = prev.findIndex((i) => i.key === key);
    const next = idx + dir;
    if (idx < 0 || next < 0 || next >= prev.length) return prev;
    const arr = [...prev];
    const tmp = arr[idx] as ImageItem;
    arr[idx] = arr[next] as ImageItem;
    arr[next] = tmp;
    return arr;
  });

// 이미지 항목 제거 (새 파일이면 object URL 정리)
const removeImageItem = (setter: Dispatch<SetStateAction<ImageItem[]>>, key: string) =>
  setter((prev) => {
    const target = prev.find((i) => i.key === key);
    if (target?.preview) URL.revokeObjectURL(target.preview);
    return prev.filter((i) => i.key !== key);
  });

// 이미지 항목 표시 URL (새 파일 우선, 없으면 기존)
const imageItemUrl = (item: ImageItem, variant: 'raw' | 'w400') =>
  item.preview || (item.image ? fileBucketUrl(item.image.url, variant) : '');

export const GiftDeliveryCategoryFormPage: FC = function GiftDeliveryCategoryFormPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const { sellerRole } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const fromBrand = searchParams.get('from') === 'brand';
  const returnTo = fromBrand ? '/gift-seller/brand' : '/gift-seller/delivery-category';
  const goBack = () => (fromBrand ? navigate(returnTo) : navigate(-1));

  const isEditMode = Boolean(categoryId);

  useGiftDeliveryCategoryHook(categoryId ?? '', sellerRole(), !isEditMode);
  const status = useRecoilValue(giftDeliveryCategoryStatusAtomFamily(categoryId ?? ''));
  const categoryData = useRecoilValue(
    giftDeliveryCategoryDataAtomFamily(categoryId ?? ''),
  );

  const setListData = useSetRecoilState(giftDeliveryCategoryListDataAtom);

  const [name, setName] = useState('');
  const [order, setOrder] = useState<number | ''>('');
  const [isEnabled, setIsEnabled] = useState(true);
  // 하위분류 이름 목록 (비어있지 않으면 이 카테고리 상품은 subCategory 필수)
  const [subCategories, setSubCategories] = useState<string[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  // 상세 이미지: 기존 이미지와 새 파일을 한 목록에서 함께 다룬다(추가/순서변경/삭제)
  const [detailImageItems, setDetailImageItems] = useState<ImageItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const detailImageOpenId = `deliveryCategoryDetailImage-${categoryId ?? 'new'}`;
  const setDetailImageUploaderOpen = useSetRecoilState(
    simpleOpenAtomFamily(`imageUploader-${detailImageOpenId}`),
  );

  // 최초 1회만 로드 — 창 포커스 refetch로 atom이 새 객체가 되어도 입력값 보존.
  const didInitRef = useRef(false);
  useEffect(() => {
    if (isEditMode && categoryData && !didInitRef.current) {
      didInitRef.current = true;
      setName(categoryData.name);
      setOrder(categoryData.order ?? '');
      setIsEnabled(categoryData.isEnabled !== false);
      setSubCategories(
        Array.isArray(categoryData.subCategories) ? categoryData.subCategories : [],
      );
      if (Array.isArray(categoryData.detailImages)) {
        setDetailImageItems(
          (categoryData.detailImages as Image[]).map((img) => ({
            key: genKey(),
            image: img,
            file: null,
            preview: '',
          })),
        );
      }
    }
  }, [isEditMode, categoryData]);

  // 업로드한 새 이미지는 기존 목록 뒤에 추가(덮어쓰지 않음)
  const onDetailImageUploaderFunc = async (_data: unknown, files: File[]) => {
    setDetailImageItems((prev) => [...prev, ...filesToImageItems(files)]);
    setDetailImageUploaderOpen(false);
  };

  const defaultImageUrl = useMemo(() => {
    if (categoryData?.image && typeof categoryData.image === 'object') {
      return (categoryData.image as Image).url;
    }
    return undefined;
  }, [categoryData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      isSubmitting ||
      (isEditMode && (status !== 'success' || !categoryData || !didInitRef.current))
    )
      return;

    if (!name.trim()) {
      alert('카테고리명을 입력해주세요.');
      return;
    }

    try {
      setIsSubmitting(true);

      let imageId: string | undefined;
      if (imageFile) {
        const uploaded = await postImage(imageFile, sellerRole());
        imageId = uploaded._id;
      }

      // 상세 이미지: 기존+신규를 화면 순서대로 전송. 신규 파일은 업로드 후 id로 변환.
      // 수정 모드에서는 빈 배열도 전송해 전체 삭제/순서변경을 반영한다.
      const detailImageIds: string[] = [];
      for (const item of detailImageItems) {
        if (item.file) {
          const uploaded = await postImage(item.file, sellerRole());
          detailImageIds.push(uploaded._id);
        } else if (item.image) {
          detailImageIds.push(item.image._id);
        }
      }

      // 하위분류: 공백 제거 + 중복 제거 (순서 유지)
      const cleanedSubCategories = subCategories
        .map((s) => s.trim())
        .filter((s, idx, arr) => s !== '' && arr.indexOf(s) === idx);

      const payload = {
        name: name.trim(),
        isEnabled,
        subCategories: cleanedSubCategories,
        ...(imageId && { image: imageId }),
        ...(order !== '' && { order: Number(order) }),
        ...(isEditMode
          ? { detailImages: detailImageIds }
          : detailImageIds.length > 0 && { detailImages: detailImageIds }),
      };

      if (isEditMode && categoryId) {
        const updated = await updateGiftDeliveryCategory(
          categoryId,
          payload,
          sellerRole(),
        );
        setListData((prev) =>
          prev.map((item) => (item._id === categoryId ? updated : item)),
        );
        alert('수정되었습니다.');
      } else {
        const created = await createGiftDeliveryCategory(payload, sellerRole());
        setListData((prev) => [...prev, created]);
        alert('등록되었습니다.');
      }

      await invalidateQueriesByUrlPart(queryClient, 'gift-delivery-category');
      navigate(returnTo);
    } catch (error) {
      console.error(error);
      alert(isEditMode ? '수정에 실패했습니다.' : '등록에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEditMode && (status === 'loading' || status === 'idle')) {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  if (isEditMode && (status === 'error' || !categoryData)) {
    return (
      <div className={`${styles.page} min-h-screen bg-gray-50 px-4 py-8 sm:px-6`}>
        <div
          role="alert"
          className="mx-auto max-w-3xl rounded-md border border-gray-200 bg-white p-8 text-center"
        >
          <h1 className="text-lg font-semibold">카테고리 정보를 불러오지 못했습니다.</h1>
          <p className="mt-2 text-sm text-gray-500">다시 조회한 후 수정해 주세요.</p>
          <div className="mt-5 flex justify-center gap-2">
            <Link
              to={returnTo}
              className="rounded border border-gray-300 px-4 py-2 text-sm"
            >
              목록으로 돌아가기
            </Link>
            <button
              type="button"
              onClick={() =>
                invalidateQueriesByUrlPart(queryClient, 'gift-delivery-category')
              }
              className="rounded bg-indigo-600 px-4 py-2 text-sm text-white"
            >
              다시 시도
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${styles.page} min-h-screen bg-gray-50 px-4 py-6 text-gray-900 sm:px-6 lg:px-8 lg:py-8`}
    >
      <div className="mx-auto max-w-4xl">
        <header className="mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label={fromBrand ? '브랜드 목록으로 돌아가기' : '뒤로 가기'}
              onClick={goBack}
              disabled={isSubmitting}
              className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100"
            >
              ←
            </button>
            <h1 className="text-2xl font-semibold tracking-tight">
              배송 카테고리 {isEditMode ? '수정' : '등록'}
            </h1>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            카테고리 설정은 연결된 모든 상품에 적용됩니다.
            {fromBrand && ' 저장 후 브랜드 목록으로 돌아갑니다.'}
          </p>
        </header>

        <form onSubmit={handleSubmit}>
          <fieldset disabled={isSubmitting}>
            <section
              className="rounded-md border border-gray-200 bg-white p-5 sm:p-6"
              aria-labelledby="category-basic-title"
            >
              <h2 id="category-basic-title" className="text-base font-semibold">
                기본 정보
              </h2>
              <p className="mb-5 mt-1 text-sm text-gray-500">
                카테고리 이름과 고객 화면의 표시 순서를 설정합니다.
              </p>
              <div className="space-y-5">
                <div>
                  <label
                    htmlFor="category-name"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    카테고리명 <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="category-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="예: 신선식품, 가전, 생활용품"
                    required
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label
                    htmlFor="category-order"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    표시 순서
                  </label>
                  <input
                    id="category-order"
                    type="number"
                    value={order}
                    onChange={(e) =>
                      setOrder(e.target.value === '' ? '' : Number(e.target.value))
                    }
                    min={0}
                    placeholder="미설정"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-400">낮을수록 앞에 표시됩니다.</p>
                </div>

                {/* 하위분류(소분류) — 이름만 관리. 1개 이상이면 이 카테고리 상품은 선택 필수 */}
                <div>
                  <h3 className="mb-1 block text-sm font-medium text-gray-700">
                    하위분류
                  </h3>
                  <div className="space-y-2">
                    {subCategories.map((sub, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          type="text"
                          aria-label={`하위분류 ${idx + 1}`}
                          value={sub}
                          onChange={(e) =>
                            setSubCategories((prev) =>
                              prev.map((s, i) => (i === idx ? e.target.value : s)),
                            )
                          }
                          placeholder="예: 비누, 섬유유연제"
                          className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        />
                        <button
                          type="button"
                          aria-label={`하위분류 ${idx + 1} 삭제`}
                          onClick={() =>
                            setSubCategories((prev) => prev.filter((_, i) => i !== idx))
                          }
                          className="shrink-0 rounded-lg border border-red-300 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          삭제
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setSubCategories((prev) => [...prev, ''])}
                    className="mt-2 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
                  >
                    + 하위분류 추가
                  </button>
                  <p className="mt-1 text-xs text-gray-400">
                    하위분류를 1개 이상 등록하면, 이 카테고리에 속한 상품은 하위분류
                    선택이 필수가 됩니다. 비워두면 하위분류 없이 사용합니다.
                  </p>
                </div>

                {/* 사용 여부 — false면 스토어에 노출되지 않음 */}
                <label className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                  <div>
                    <span className="text-sm font-medium text-gray-700">사용 여부</span>
                    <p className="text-xs text-gray-400">
                      끄면 스토어(고객 화면)에 노출되지 않습니다.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    aria-label="카테고리 사용 여부"
                    checked={isEnabled}
                    onChange={(e) => setIsEnabled(e.target.checked)}
                    className="h-5 w-5"
                  />
                </label>
              </div>
            </section>

            <section className="mt-4 rounded-md border border-gray-200 bg-white p-5 sm:p-6">
              <h2 className="mb-3 text-base font-semibold">썸네일 이미지</h2>
              <div className="max-w-[300px]">
                <ImageUploadBox
                  title="썸네일 이미지"
                  setFile={setImageFile}
                  defaultImageUrl={defaultImageUrl}
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                카테고리 목록에서 사용되는 대표 썸네일 이미지입니다.
              </p>
            </section>

            <section className="mt-4 rounded-md border border-gray-200 bg-white p-5 sm:p-6">
              <h2 className="mb-1 text-base font-semibold">상세(맥락설명) 이미지</h2>
              <p className="mb-2 text-xs text-gray-500">
                이 카테고리에 속한 상품의 상품상세페이지에서, 상품 상세 이미지보다 먼저
                세로로 표시되는 맥락설명용 이미지입니다. 업로드하면 기존 이미지 뒤에
                추가되며, ▲▼ 로 순서를 바꿀 수 있습니다.
              </p>
              <div className="max-w-[400px]">
                <Button
                  type="button"
                  className="rounded bg-indigo-600 px-3 py-1.5 text-sm text-white"
                  onClick={() => setDetailImageUploaderOpen(true)}
                >
                  이미지 추가
                </Button>

                {detailImageItems.length > 0 ? (
                  <div className="mt-2 space-y-1">
                    {detailImageItems.map((item, idx) => (
                      <div key={item.key} className="relative">
                        <img
                          src={imageItemUrl(item, 'w400')}
                          alt={`상세이미지 ${idx + 1}`}
                          className="w-full rounded object-cover"
                        />
                        <span className="absolute left-1 top-1 rounded bg-black/50 px-1 text-xs text-white">
                          {idx + 1}
                        </span>
                        {item.file && (
                          <span className="absolute left-7 top-1 rounded bg-blue-500 px-1 text-[10px] text-white">
                            new
                          </span>
                        )}
                        <div className="absolute right-1 top-1 flex gap-0.5 rounded bg-black/50 px-0.5 py-0.5">
                          <button
                            type="button"
                            aria-label={`상세 이미지 ${idx + 1} 위로 이동`}
                            disabled={idx === 0}
                            onClick={() =>
                              moveImageItem(setDetailImageItems, item.key, -1)
                            }
                            className="px-1 text-xs text-white disabled:text-white/30"
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            aria-label={`상세 이미지 ${idx + 1} 아래로 이동`}
                            disabled={idx === detailImageItems.length - 1}
                            onClick={() =>
                              moveImageItem(setDetailImageItems, item.key, 1)
                            }
                            className="px-1 text-xs text-white disabled:text-white/30"
                          >
                            ▼
                          </button>
                          <button
                            type="button"
                            aria-label={`상세 이미지 ${idx + 1} 삭제`}
                            onClick={() => removeImageItem(setDetailImageItems, item.key)}
                            className="px-1 text-xs text-red-300 hover:text-red-200"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-gray-400">
                    등록된 상세 이미지가 없습니다.
                  </p>
                )}
              </div>
            </section>

            <div className="sticky bottom-0 z-10 mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-white px-5 py-4 shadow-[0_-4px_12px_rgba(0,0,0,0.03)]">
              <p className="text-xs text-gray-500">
                {isSubmitting
                  ? '카테고리 정보를 저장하고 있습니다.'
                  : '변경한 내용은 저장 후 적용됩니다.'}
              </p>
              <div className="ml-auto flex gap-2">
                <Button
                  type="button"
                  onClick={goBack}
                  disabled={isSubmitting}
                  className="rounded border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 disabled:opacity-40"
                >
                  취소
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="rounded bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
                >
                  {isSubmitting ? '저장 중...' : isEditMode ? '수정하기' : '등록하기'}
                </Button>
              </div>
            </div>
          </fieldset>
        </form>
      </div>

      {/* 상세 이미지 업로더 모달 */}
      <ImageUploader
        openId={detailImageOpenId}
        title="상세 이미지 업로드"
        onSubmit={onDetailImageUploaderFunc}
        useName={false}
      />
    </div>
  );
};
